import React, { useState, useCallback } from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import AlternateSizeGuide from '~/shared/components/AlternateSizeGuide';
import CloseButton from '~/shared/components/CloseButton';
import FableticsModal from '~/shared/components/FableticsModal';
import {
  getMensBottomsProductCategories,
  getMensProductCategories,
  getMensTopsProductCategories,
} from '~/shared/utils/productCategoryHelpers';
import {
  AssetContainer,
  ImageAsset,
  useAsset,
} from '~/techstyle-shared/react-assets';
import {
  Button,
  mobile,
  desktop,
  useBreakpoint,
} from '~/techstyle-shared/react-components';
import { useIntl, FormattedMessage } from '~/techstyle-shared/react-intl';

import WomenFitGuide from '../WomenFitGuide';

const dialogStyle = css`
  ${desktop`
    max-height: calc(100vh - 128px);
  `}
`;

const LinkButton = styled(Button).attrs({
  variant: 'unstyled',
})`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 16px;
  letter-spacing: ${-0.35 / 14}em;
  text-decoration: underline;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.sublabelBlack};
`;

const ModalCloseButton = styled(CloseButton)`
  position: absolute;
  top: 0px;
  right: 0px;
  padding: 18px;
`;

const ImageWrapper = styled.div`
  max-width: 800px;
  ${mobile`
    margin: 52px 0;
  `}
`;

const Image = styled(ImageAsset)`
  max-width: 800px;
`;

const modalContentWrapperStyles = css`
  // The FableticsModal makes this a flex display, which causes an extraneous horizontal scrollbar
  // to appear.
  display: unset;
`;

const transitionProps = {
  enter: { opacity: 1, transform: 'scale(1, 1)' },
  from: { opacity: 0, transform: 'scale(0.5, 0.5)' },
  leave: { opacity: 0, transform: 'scale(0.5, 0.5)' },
};

const mensIds = getMensProductCategories();
const mensTopIds = getMensTopsProductCategories();
const mensBottomIds = getMensBottomsProductCategories();

const MensFabricGuide = ({ categoryIdList }) => {
  const { isMobile } = useBreakpoint();
  const isMensTop =
    categoryIdList &&
    categoryIdList.filter((value) => mensTopIds.indexOf(value) >= 0).length > 0;
  const isMensBottom =
    categoryIdList &&
    categoryIdList.filter((value) => mensBottomIds.indexOf(value) >= 0).length >
      0;
  const isMensOther = !isMensTop && !isMensBottom;
  const showMensTopAsset = isMensTop || isMensOther;
  const showMensBottomAsset = isMensBottom || isMensOther;

  // mens size guide asset
  const assetContainer = isMobile
    ? 'Mens_Size_Guide_Lightbox_mobile'
    : 'Mens_Size_Guide_Lightbox';

  const { data: assetData } = useAsset(assetContainer, {
    multi: true,
  });
  const assetList = assetData?.assets || [];
  const assets = {
    top: assetList.find(({ label }) => label.toLowerCase().includes('tops')),
    bottom: assetList.find(({ label }) =>
      label.toLowerCase().includes('bottom')
    ),
  };

  return (
    <ImageWrapper>
      {showMensTopAsset && assets.top ? (
        <Image asset={assets.top} aspectRatio="auto" />
      ) : null}
      {showMensBottomAsset && assets.bottom ? (
        <Image asset={assets.bottom} aspectRatio="auto" />
      ) : null}
    </ImageWrapper>
  );
};

MensFabricGuide.propTypes = {
  categoryIdList: PropTypes.array,
};

const KidsSizeGuide = () => {
  return (
    <ImageWrapper>
      <AssetContainer name="FL_Kids_Size_Guide" />
    </ImageWrapper>
  );
};

// I would love for this and the sizeFabricGuideModal to be merged at some point, but for now Fabletics needs
// its own for us to cutover Fabletics to this codebase
const SizeFabricGuideFableticsModal = ({
  categoryIdList,
  useAlternate,
  isKids,
  isScrubs,
  isPets,
}) => {
  const [isModalOpen, setModalOpen] = useState(false);

  const openModal = useCallback(() => {
    setModalOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const intl = useIntl();
  const closeLabel = intl.formatMessage({
    id: 'global_cta.close',
    defaultMessage: 'Close',
  });

  const isMens = categoryIdList?.some((categoryId) =>
    mensIds?.includes(categoryId)
  );

  let sizeGuideModal = null;
  if (useAlternate) {
    sizeGuideModal = (
      <AlternateSizeGuide isMens={isMens} isScrubs={isScrubs} isPets={isPets} />
    );
  } else if (isMens) {
    sizeGuideModal = <MensFabricGuide categoryIdList={categoryIdList} />;
  } else if (isKids) {
    sizeGuideModal = <KidsSizeGuide />;
  } else {
    sizeGuideModal = <WomenFitGuide categoryIdList={categoryIdList} />;
  }

  return (
    <>
      <LinkButton onClick={openModal} data-autotag="pdp-size-guide-link">
        {!isMens ? (
          <FormattedMessage
            id="site_pdp.guides"
            defaultMessage="Size and Fabric Guides"
          />
        ) : (
          <FormattedMessage
            id="site_pdp.mens_guide_item_pdp"
            defaultMessage="View Men’s Size Guide"
          />
        )}
      </LinkButton>

      <FableticsModal
        contentWrapperStyles={modalContentWrapperStyles}
        dialogStyle={dialogStyle}
        isOpen={isModalOpen}
        onExit={closeModal}
        closeButton={<ModalCloseButton aria-label={closeLabel} />}
        showCloseButton
        transitionEnter={transitionProps.enter}
        transitionFrom={transitionProps.from}
        transitionLeave={transitionProps.leave}
        title={intl.formatMessage({
          id: 'site_pdp.guides',
          defaultMessage: 'Size and Fabric Guides',
        })}
        variant="unpadded"
      >
        {sizeGuideModal}
      </FableticsModal>
    </>
  );
};

SizeFabricGuideFableticsModal.propTypes = {
  categoryIdList: PropTypes.array,
  isKids: PropTypes.bool,
  isPets: PropTypes.bool,
  isScrubs: PropTypes.bool,
  useAlternate: PropTypes.bool, // use the alternate size guide for this product
};

export default SizeFabricGuideFableticsModal;
